import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, CheckCircle2, ChevronRight, Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

function InterestForm({ cat, role }) {
  const supplier = role === "supplier";
  const [form, setForm] = useState({ who: "", email: "", details: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const p = `interest-${role}`;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/category-interest", {
        category_id: cat.id, role, email: form.email.trim(),
        company: supplier ? form.who.trim() : "", name: supplier ? "" : form.who.trim(), details: form.details.trim(),
      });
      setDone(true);
      toast.success(data.already_registered ? "You're already on the list — details updated" : "You're on the list");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save — please try again");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex items-start gap-3 text-[#264839]" data-testid={`${p}-success`}>
        <CheckCircle2 className="w-5 h-5 mt-0.5" />
        <div>
          <div className="font-medium">Thanks — you're first in line.</div>
          <div className="text-sm text-[#4A5550] mt-1">We've emailed a confirmation to {form.email}. {supplier ? "We'll tell you the moment this category opens so you can set up your listing." : "We'll let you know as soon as suppliers are live."}</div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`${p}-form`}>
      <div>
        <label className="block text-xs font-medium text-[#4A5550] mb-1">{supplier ? "Company name" : "Your name"} *</label>
        <input required value={form.who} onChange={set("who")} className="w-full px-3 py-2.5 border border-[#C4CFC9] bg-white text-sm rounded-md focus:outline-none focus:border-[#264839]" data-testid={`${p}-who`} />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#4A5550] mb-1">Email *</label>
        <input required type="email" value={form.email} onChange={set("email")} className="w-full px-3 py-2.5 border border-[#C4CFC9] bg-white text-sm rounded-md focus:outline-none focus:border-[#264839]" data-testid={`${p}-email`} />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#4A5550] mb-1">{supplier ? "What do you supply?" : "What products or services are you looking to source?"} *</label>
        <textarea required rows={3} value={form.details} onChange={set("details")} className="w-full px-3 py-2.5 border border-[#C4CFC9] bg-white text-sm rounded-md focus:outline-none focus:border-[#264839]" data-testid={`${p}-details`} />
      </div>
      <button type="submit" disabled={busy} className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${supplier ? "bg-[#264839] hover:bg-[#1B3329] text-white" : "border border-[#264839] text-[#264839] hover:bg-[#264839] hover:text-white"}`} data-testid={`${p}-submit`}>
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} {supplier ? "Register my business" : "Register my interest"}
      </button>
    </form>
  );
}

/** Shared layout for every coming-soon top-level category — never a dead end. */
export default function ComingSoonCategory({ cat }) {
  const imgUrl = cat.image_url && cat.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${cat.image_url}` : cat.image_url;
  return (
    <div data-testid="coming-soon-category">
      <SEO title={`${cat.name} — coming soon`} description={cat.intro || cat.description || `${cat.name} suppliers for accommodation properties — register your interest.`} path={`/category/${cat.slug || cat.id}`} />
      <section className="relative bg-[#11211A] text-white overflow-hidden">
        {imgUrl && <div className="absolute inset-0 bg-cover bg-center opacity-35" style={{ backgroundImage: `url('${imgUrl}')` }} aria-hidden="true" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#11211A] via-[#11211A]/85 to-[#11211A]/40" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="flex items-center text-sm text-white/60 mb-6">
            <Link to="/categories" className="hover:text-white">Categories</Link>
            <ChevronRight className="w-3 h-3 mx-2" />
            <span className="text-white/80">{cat.name}</span>
          </div>
          <div className="tracking-overline !text-[#D8B98A] mb-3" data-testid="coming-soon-overline">Coming soon to AccommodationSuppliers.com</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl" data-testid="coming-soon-title">{cat.name}</h1>
          {(cat.intro || cat.description) && <p className="text-base md:text-lg text-white/80 mt-6 max-w-2xl leading-relaxed" data-testid="coming-soon-intro">{cat.intro || cat.description}</p>}
        </div>
      </section>

      <section className="bg-[#F0EFEA] py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl md:text-4xl text-[#11211A] tracking-tight" data-testid="coming-soon-headline">Be first: register your interest.</h2>
          <p className="text-[#4A5550] mt-3 max-w-2xl">Two separate lists — one for suppliers who want to list when this category opens, one for buyers who want to be told when suppliers are live.</p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mt-10">
            <div className="bg-white rounded-2xl border border-[#E8ECE9] p-6 sm:p-8 shadow-sm" data-testid="coming-soon-supplier-card">
              <div className="flex items-center gap-2 text-[#264839] text-xs uppercase tracking-[0.2em] font-semibold mb-2"><Building2 className="w-4 h-4" /> For suppliers</div>
              <h3 className="font-serif text-2xl text-[#11211A]">List your business when this opens</h3>
              <p className="text-sm text-[#7D8C85] mt-2 mb-6">Get a head start — we'll tell you the moment {cat.name} opens so your listing is live before buyers arrive.</p>
              <InterestForm cat={cat} role="supplier" />
            </div>
            <div className="bg-white rounded-2xl border border-[#E8ECE9] p-6 sm:p-8 shadow-sm" data-testid="coming-soon-buyer-card">
              <div className="flex items-center gap-2 text-[#B35C4A] text-xs uppercase tracking-[0.2em] font-semibold mb-2"><Search className="w-4 h-4" /> For hotels &amp; buyers</div>
              <h3 className="font-serif text-2xl text-[#11211A]">Tell us what you're looking for</h3>
              <p className="text-sm text-[#7D8C85] mt-2 mb-6">Your answers shape which suppliers we bring on first. Free for buyers, always.</p>
              <InterestForm cat={cat} role="buyer" />
            </div>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <span className="text-[#7D8C85]">Sourcing today?</span>
            <Link to="/category/linen-textiles" className="inline-flex items-center gap-1.5 text-[#264839] font-medium hover:underline underline-offset-4" data-testid="coming-soon-linen-link">Explore Linen, Textiles &amp; Soft Furnishings suppliers <ChevronRight className="w-4 h-4" /></Link>
            <Link to="/categories" className="text-[#7D8C85] hover:text-[#264839]" data-testid="coming-soon-all-link">All categories</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
